import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Users, MessageCircle, Shield, Sparkles, Moon, Sun, Calendar, Video, MapPin, Gift, Zap, Star, ChevronDown, ChevronUp } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  // Sync with actual theme on mount
  useEffect(() => {
    const checkTheme = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    checkTheme();
    
    // Listen for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  const toggleTheme = () => {
    const root = document.documentElement;
    const newTheme = isDark ? 'light' : 'dark';
    
    if (newTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    root.style.colorScheme = newTheme;
    localStorage.setItem('unitylove_theme', newTheme);
    setIsDark(!isDark);
  };

  const heroImages = [
    "https://images.unsplash.com/photo-1724383986262-893dd74060a6",
    "https://images.unsplash.com/photo-1739302750675-042ed497a429?w=800&q=80",
    "https://images.pexels.com/photos/6657701/pexels-photo-6657701.jpeg?auto=compress&cs=tinysrgb&w=800"
  ];

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Handle URL hash for shareable feature state
  useEffect(() => {
    if (window.location.hash === '#all-features') {
      setShowAllFeatures(true);
      setTimeout(() => {
        const el = document.getElementById('features');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, []);

  const toggleFeatures = () => {
    const newState = !showAllFeatures;
    setShowAllFeatures(newState);
    window.history.replaceState(null, '', newState ? '#all-features' : window.location.pathname);
  };

  const features = [
    {
      icon: MessageCircle,
      title: "Unity Wingman (AI Assist)",
      desc: "Get gentle AI-powered help with openers, replies, and profile polish—while still sounding like you. Free assists weekly, more for Elite.",
    },
    {
      icon: Users,
      title: "LoveLink & Your Circle",
      desc: "Share your LoveLink, grow your Circle, and receive rewards when your people join and upgrade. Community-powered growth, not pressure.",
    },
    {
      icon: Shield,
      title: "Elite & Lifestyle Lounge",
      desc: "Unlock Elite perks, extended Lounge time, and boosted visibility—built on verification, safety, and respect for every type of relationship.",
    },
    {
      icon: Calendar,
      title: "Speed Dating (Text + Video)",
      desc: "Low-pressure, conversation-first speed dating—with hosted video sessions and curated rooms by vibe and region.",
    },
    {
      icon: Video,
      title: "Video Lounge & Live Rooms",
      desc: "Join live hangouts designed for real energy, not endless swiping. Meet people in a safer, more human way.",
    },
    {
      icon: MapPin,
      title: "Local-First Discovery",
      desc: "Distance-based discovery keeps your matches intentional—so you're not accidentally matching across the country.",
    },
    {
      icon: Star,
      title: "Her Space",
      desc: "A women-centered community space for conversation, culture, and connection—designed for comfort and respect.",
    },
    {
      icon: Gift,
      title: "LoveCoins, Rewards & Referrals",
      desc: "Earn perks through participation and invitations. Built to reward real community building—not spammy tactics.",
    },
    {
      icon: Zap,
      title: "Boosts & Visibility Tools",
      desc: "Boost your presence when you want more momentum—without turning the platform into a chaotic swipe casino.",
    },
  ];

  const FeatureCard = ({ icon: Icon, title, desc, index }) => (
    <div
      className={`text-center p-8 rounded-2xl card-hover ul-feature-card ${
        isDark ? 'bg-white/5 border border-white/10' : 'bg-gradient-sunset'
      }`}
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <div
        className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${
          isDark ? 'bg-rose-500/20' : 'bg-rose-100'
        }`}
      >
        <Icon className={`w-8 h-8 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
      </div>
      <h3 className={`font-playfair text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        {title}
      </h3>
      <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
        {desc}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen relative bg-white dark:bg-slate-950">
      <style>{`
        @keyframes ulFadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .ul-feature-card {
          opacity: 0;
          animation: ulFadeUp 420ms ease-out forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .ul-feature-card { animation: none; opacity: 1; }
        }
      `}</style>
      {/* Background dimmer to prevent washed-out text */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-white/45 dark:bg-slate-950/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/5 dark:to-black/20" />
      </div>

      <div className={`relative text-slate-900 dark:text-slate-50 transition-colors duration-300`}>
      {/* Navigation - Theme aware */}
      <nav className={`backdrop-blur-sm border-b sticky top-0 z-50 transition-colors duration-300 ${
        isDark 
          ? 'bg-slate-900/90 border-white/10' 
          : 'bg-white/90 border-rose-100'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <Heart className={`w-8 h-8 fill-current ${isDark ? 'text-rose-500' : 'text-rose-600'}`} />
              <span className={`font-playfair text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Unity Love
              </span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('features')}
                className={`font-medium transition-colors ${
                  isDark ? 'text-gray-300 hover:text-rose-400' : 'text-gray-700 hover:text-rose-600'
                }`}
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection('community')}
                className={`font-medium transition-colors ${
                  isDark ? 'text-gray-300 hover:text-rose-400' : 'text-gray-700 hover:text-rose-600'
                }`}
              >
                Community
              </button>
              <a href="/safety" className={`font-medium transition-colors ${
                isDark ? 'text-gray-300 hover:text-rose-400' : 'text-gray-700 hover:text-rose-600'
              }`}>
                Safety
              </a>
            </div>

            <div className="flex items-center space-x-4">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                  isDark 
                    ? 'bg-slate-800 hover:bg-slate-700 text-amber-400' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                }`}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label="Toggle theme"
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              <button
                onClick={() => navigate('/auth')}
                className={`font-semibold transition-colors ${
                  isDark ? 'text-rose-400 hover:text-rose-300' : 'text-rose-600 hover:text-rose-700'
                }`}
                data-testid="login-btn"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="btn-primary"
                data-testid="get-started-btn"
              >
                Start Your Journey
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-b from-rose-500/5 via-transparent to-transparent' : ''}`} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="fade-in">
              <h1 className={`font-playfair text-5xl lg:text-6xl font-bold mb-6 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Start Your Journey
                <span className={`block ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>
                  With Unity Love
                </span>
              </h1>
              
              <p className={`text-xl mb-8 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                A safe, respectful platform for African American and African couples, 
                singles, and poly families seeking meaningful connections. Build authentic 
                relationships based on shared values and cultural understanding—whether 
                you're exploring poly life or looking for your perfect match.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/auth')}
                  className="btn-primary text-lg px-8 py-4"
                  data-testid="hero-join-btn"
                >
                  Start Your Journey
                </button>
                <button
                  onClick={() => scrollToSection('features')}
                  className={`text-lg px-8 py-4 border-2 rounded-full font-semibold transition-all ${
                    isDark 
                      ? 'border-rose-500 text-rose-400 hover:bg-rose-500 hover:text-white' 
                      : 'border-rose-600 text-rose-600 hover:bg-rose-600 hover:text-white'
                  }`}
                >
                  Explore Features
                </button>
              </div>

              {/* Stats removed: previous "5K+ Active Members / 1.2K+ Successful Matches / 98% Safety Rating"
                  were placeholder numbers, not real metrics. Re-enable only when backed by audited data. */}
            </div>

            {/* Right Images */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="card-hover">
                    <img
                      src={heroImages[0]}
                      alt="Happy couple in traditional attire"
                      className={`w-full h-64 object-cover rounded-2xl ${isDark ? 'ring-1 ring-white/10' : 'shadow-lg'}`}
                    />
                  </div>
                  <div className="card-hover">
                    <img
                      src={heroImages[1]}
                      alt="Romantic couple"
                      className={`w-full h-48 object-cover rounded-2xl ${isDark ? 'ring-1 ring-white/10' : 'shadow-lg'}`}
                    />
                  </div>
                </div>
                <div className="mt-8">
                  <div className="card-hover">
                    <img
                      src={heroImages[2]}
                      alt="Elegant couple"
                      className={`w-full h-80 object-cover rounded-2xl ${isDark ? 'ring-1 ring-white/10' : 'shadow-lg'}`}
                    />
                  </div>
                </div>
              </div>
              
              {/* Floating elements */}
              <div className={`absolute -top-4 -right-4 p-4 rounded-full ${
                isDark 
                  ? 'bg-rose-500/20 border border-rose-500/30' 
                  : 'bg-rose-100'
              }`}>
                <Sparkles className={`w-6 h-6 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
              </div>
              <div className={`absolute -bottom-4 -left-4 p-4 rounded-full ${
                isDark 
                  ? 'bg-amber-500/20 border border-amber-500/30' 
                  : 'bg-amber-100'
              }`}>
                <Heart className={`w-6 h-6 fill-current ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className={`py-20 ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`font-playfair text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Built for Intentional Connection
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Unity Love combines culture, community, and AI-powered tools to help you
              build deeper, more intentional relationships.
            </p>
            <p className={`mt-3 text-sm tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              Dating, community, and culture — designed to work together.
            </p>
          </div>

          {(() => {
            const visible = showAllFeatures ? features : features.slice(0, 6);

            return (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {visible.map((f, idx) => (
                    <FeatureCard
                      key={f.title}
                      icon={f.icon}
                      title={f.title}
                      desc={f.desc}
                      index={idx}
                    />
                  ))}
                </div>

                <div className="mt-10 flex flex-col items-center gap-6">
                  <button
                    onClick={toggleFeatures}
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border transition-all ${
                      isDark
                        ? 'border-white/15 text-gray-200 hover:bg-white/10'
                        : 'border-gray-300 text-gray-800 hover:bg-gray-100'
                    }`}
                    data-testid="features-progressive-toggle"
                  >
                    {showAllFeatures ? (
                      <>
                        <ChevronUp className="w-5 h-5" />
                        Show fewer features
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-5 h-5" />
                        Show more features
                      </>
                    )}
                  </button>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <button
                      onClick={() => navigate('/how-it-works')}
                      className="btn-primary text-lg px-8 py-4"
                      data-testid="features-see-more-btn"
                    >
                      See the Full Breakdown
                    </button>
                    <button
                      onClick={() => navigate('/auth')}
                      className={`text-lg px-8 py-4 border-2 rounded-full font-semibold transition-all ${
                        isDark
                          ? 'border-rose-500 text-rose-400 hover:bg-rose-500 hover:text-white'
                          : 'border-rose-600 text-rose-600 hover:bg-rose-600 hover:text-white'
                      }`}
                      data-testid="features-join-btn"
                    >
                      Create Your Profile
                    </button>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* Community Section */}
      <section id="community" className={`py-20 ${
        isDark 
          ? 'bg-gradient-to-b from-slate-950 via-rose-950/20 to-slate-950' 
          : 'bg-gradient-rose-gold'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className={`font-playfair text-4xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Built for Our Community
              </h2>
              <p className={`text-lg mb-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Unity Love celebrates African American and African culture, providing 
                a space where shared heritage and values create the foundation for 
                lasting relationships—whether you're a couple, single, or building a poly family.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-rose-500' : 'bg-rose-600'}`}></div>
                  <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Cultural understanding and respect</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-rose-500' : 'bg-rose-600'}`}></div>
                  <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Shared values and traditions</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-rose-500' : 'bg-rose-600'}`}></div>
                  <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Supportive community environment</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-rose-500' : 'bg-rose-600'}`}></div>
                  <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Modern approach to relationships</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1765292783732-ca81c5b69e25?w=1200&q=85"
                alt="Black couple sharing a moment"
                className={`w-full h-96 object-cover rounded-2xl ${isDark ? 'ring-1 ring-white/10' : 'shadow-xl'}`}
              />
              <div className={`absolute inset-0 rounded-2xl ${
                isDark 
                  ? 'bg-gradient-to-t from-slate-950/60 to-transparent' 
                  : 'bg-gradient-to-t from-black/20 to-transparent'
              }`}></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-20 ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className={`font-playfair text-4xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Ready to Find Your Connection?
          </h2>
          <p className={`text-xl mb-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Join a growing community of couples and singles building meaningful 
            relationships in a culture-first space.
          </p>
          
          <button
            onClick={() => navigate('/auth')}
            className="btn-primary text-lg px-12 py-4"
            data-testid="final-cta-btn"
          >
            Start Your Journey
          </button>
        </div>
      </section>

      {/* Footer handled by GlobalFooter in App.js */}
      </div>
    </div>
  );
};

export default LandingPage;
